import React from 'react'
import { Typography, useTheme, alpha, Box, Tooltip, Paper } from '@mui/material'
import { Grid } from '@mui/system'

import { constants } from '../../config'
import { buildDashboardUnitsConfig, getTabStats, readStatValue } from '../../utils/dashboardUnits'
import { getBillableMode } from '../../utils/dashboardMath'

const { catNames } = constants

const supportColor = (theme) => theme.custom?.keysys?.blue ?? theme.palette.primary.main
const upgradesColor = (theme) => theme.palette.warning.main
const vulnerabilitiesColor = (theme) => theme.palette.error.main

const UnitsBreakdownPopper = ({ unitsPercentages }) => {
  const theme = useTheme()
  if (!unitsPercentages) return null
  const p = unitsPercentages
  const total = Number(p.billableSum ?? 0) + Number(p.nonBillableSum ?? 0)
  const row = (label, pct, sum, color) => (
    <Box
      key={label}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        py: 0.75,
        px: 1.25,
        borderRadius: 1,
        backgroundColor: alpha(color, 0.08),
        borderLeft: `3px solid ${color}`
      }}
    >
      <Typography variant='caption' sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
        {label}
      </Typography>
      <Typography variant='caption' sx={{ fontVariantNumeric: 'tabular-nums', color: alpha(theme.palette.text.primary, 0.9) }}>
        {Number(pct ?? 0).toFixed(1)}% · {Number(sum ?? 0).toLocaleString()} SP
      </Typography>
    </Box>
  )
  return (
    <Paper
      elevation={8}
      sx={{
        p: 1.25,
        minWidth: 220,
        borderRadius: 2,
        backgroundColor: alpha(theme.palette.background.paper, 0.98),
        border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
        boxShadow: `0 8px 24px ${alpha('#000', 0.2)}`
      }}
    >
      <Typography variant='overline' sx={{ color: theme.palette.text.secondary, fontWeight: 700, letterSpacing: 0.8, display: 'block', mb: 1 }}>
        SP by type
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {row('Support', p.supportPct, p.supportSum, supportColor(theme))}
        {row('Upgrades', p.upgradesPct, p.upgradesSum, upgradesColor(theme))}
        {row('Vulnerabilities', p.vulnerabilitiesPct, p.vulnerabilitiesSum, vulnerabilitiesColor(theme))}
      </Box>
      {total > 0 && (
        <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant='caption' sx={{ color: theme.palette.success.main, fontWeight: 600 }}>Billable</Typography>
            <Typography variant='caption' sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(p.billablePct ?? 0).toFixed(1)}%</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant='caption' sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Non-billable</Typography>
            <Typography variant='caption' sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(p.nonBillablePct ?? 0).toFixed(1)}%</Typography>
          </Box>
        </Box>
      )}
    </Paper>
  )
}

const StatLine = ({ label, value, valueColor, tooltip }) => {
  const theme = useTheme()
  const safeValue = value ?? ''
  const resolvedColor = valueColor && (() => {
    const [scope, key] = String(valueColor).split('.')
    return theme.palette[scope]?.[key]
  })()
  const color = resolvedColor ?? theme.palette.text.primary

  const content = (
    <Grid item>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 1,
          border: `1px solid ${valueColor ? alpha(color, 0.35) : alpha(theme.palette.divider, 0.5)}`,
          backgroundColor: alpha(theme.custom.keysys.dark, 0.2),
          boxShadow: `0 4px 12px ${alpha('#000', 0.15)}, 0 2px 4px ${alpha('#000', 0.1)}`,
          transition: 'transform 140ms ease, box-shadow 140ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 6px 16px ${alpha('#000', 0.2)}, 0 3px 6px ${alpha('#000', 0.15)}`
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography
            variant='overline'
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.1,
              letterSpacing: 0.6,
              fontSize: '0.7rem'
            }}
          >
            {label}
          </Typography>

          <Typography
            variant='h6'
            sx={{
              fontWeight: 900,
              color,
              lineHeight: 1.1,
              fontVariantNumeric: 'tabular-nums',
              fontSize: '1.1rem'
            }}
          >
            {safeValue}
          </Typography>
        </Box>
      </Box>
    </Grid>
  )

  return tooltip
    ? (
      <Tooltip
        title={tooltip}
        placement='top'
        arrow
        enterDelay={300}
        leaveDelay={0}
        slotProps={{
          popper: {
            sx: {
              '& .MuiTooltip-tooltip': {
                p: 0,
                backgroundColor: 'transparent',
                maxWidth: 'none'
              },
              '& .MuiTooltip-arrow': {
                color: 'background.paper'
              }
            }
          }
        }}
      >
        <span style={{ display: 'inline-block' }}>{content}</span>
      </Tooltip>
      )
    : content
}

const DashboardCounter = (props) => {
  const { activeTab = '', catOptions = {}, graphStatistics = {}, billableSelected = {} } = props

  const billableMode = getBillableMode(billableSelected)
  const cfg = React.useMemo(
    () => buildDashboardUnitsConfig({ catNames, catOptions, billableMode }),
    [catOptions, billableMode]
  )
  const tabCfg = cfg?.[activeTab]
  const tabStats = getTabStats(graphStatistics, activeTab)

  if (!activeTab || !tabCfg) return null

  const lines = Array.isArray(tabCfg.lines) ? tabCfg.lines : []
  const trailing = tabCfg.trailing

  const breakdownPopper = tabStats?.UnitsPercentages
    ? <UnitsBreakdownPopper unitsPercentages={tabStats.UnitsPercentages} />
    : undefined

  return (
    <Grid container spacing={1.5} alignItems='center' justifyContent='center'>
      {lines.map((line) => {
        const label = typeof line.label === 'function' ? line.label(activeTab) : String(line.label || '')
        const raw = readStatValue(tabStats, line.key)
        const formattedValue = typeof line.format === 'function' ? line.format(raw, { activeTab }) : String(raw || '0')
        const showBreakdown = line.tooltipKey === 'UnitsPercentages' && breakdownPopper

        return (
          <StatLine
            key={`line-${line.key}`}
            label={label}
            value={formattedValue}
            valueColor={line.valueColor}
            tooltip={showBreakdown ? breakdownPopper : undefined}
          />
        )
      })}

      {trailing && (
        <StatLine
          key={`trailing-${trailing.key}`}
          label={typeof trailing.label === 'function' ? trailing.label(activeTab) : String(trailing.label || '')}
          value={(() => {
            const raw = readStatValue(tabStats, trailing.key)
            return typeof trailing.format === 'function' ? trailing.format(raw, { activeTab }) : String(raw || '0')
          })()}
          valueColor={trailing.valueColor}
          tooltip={trailing.tooltipKey === 'UnitsPercentages' ? breakdownPopper : undefined}
        />
      )}
    </Grid>
  )
}

export { DashboardCounter }
